import { CSSProperties } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { InputActionMeta } from 'react-select';
import Creatable from 'react-select/creatable';
import { AsyncPaginate, withAsyncPaginate } from 'react-select-async-paginate';

import { Visible } from 'coreOld/components';
import { DropdownIndicator, FormError, Icon } from 'coreOld/elements';
import { IOption } from 'coreOld/types';

import { FormatLabelStyled, IconWrapper, InputWrapper } from './asyncSelectStyle';

interface IProps {
  key?: string;
  name: string;
  pageSize?: number;
  placeholder?: string;
  value?: string;
  searchText?: string;
  defaultValue?: string;
  isClearable?: boolean;
  showDropdownIcon?: boolean;
  getLabel?: (option) => string;
  getValue?: (option) => string;
  formatOptionLabel?: (option, { selectValue }) => (string | JSX.Element)[];
  required?: boolean;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  onIconClick?: (event, searchText?: string) => void;
  disabled?: boolean;
  mapDataToFilters?;
  isMulti?;
  loadOptions;
  onChange?;
  onInputChange?: (inputValue: string, actionMeta: InputActionMeta) => void;
  customStyles?;
  iconStyle?: CSSProperties;
  control?;
  onBlur?;
  onKeyDown?;
  onFocus?: () => void;
  autoFocus?: boolean;
  isCreatable?: boolean;
  createOption?: (label: string) => IOption;
  onCreateOption?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => JSX.Element;
  error?: FieldError | string;
  showFieldError?: boolean;
}

const CreatableAsyncPaginate = withAsyncPaginate(Creatable);

const handleCreateOption = (label: string) => ({
  label,
  value: label.toLowerCase().replace(/\W/g, ''),
});

const handleFormatCreateLabel = (inputValue: string) => (
  <FormatLabelStyled>
    <Icon name={Icon.STRONG_PLUS} />
    {inputValue}
  </FormatLabelStyled>
);

const AsyncSelectView = ({
  key,
  value,
  searchText,
  onChange,
  onInputChange,
  getLabel,
  getValue,
  formatOptionLabel,
  customStyles,
  iconStyle,
  placeholder,
  name,
  loadOptions,
  showDropdownIcon,
  isMulti,
  isClearable,
  startIcon,
  endIcon,
  disabled,
  onIconClick,
  control,
  defaultValue,
  mapDataToFilters,
  onBlur,
  onKeyDown,
  onFocus,
  autoFocus,
  isCreatable,
  createOption = handleCreateOption,
  onCreateOption,
  formatCreateLabel = handleFormatCreateLabel,
  error,
  showFieldError,
}: IProps) => {
  const dropdownIndicator = showDropdownIcon && !disabled ? DropdownIndicator : null;

  const commonProps = {
    key,
    loadOptions,
    isMulti,
    isClearable,
    isDisabled: disabled,
    closeMenuOnSelect: true,
    getOptionValue: getValue,
    getOptionLabel: getLabel,
    placeholder,
    additional: { page: 1 },
    debounceTimeout: 500,
    styles: customStyles,
    components: { DropdownIndicator: dropdownIndicator },
    onInputChange,
    inputValue: searchText,
    formatOptionLabel,
    onFocus,
    autoFocus,
    createOption,
    onCreateOption,
    formatCreateLabel,
  };

  let AsyncPaginateInput = AsyncPaginate;
  if (isCreatable) {
    AsyncPaginateInput = CreatableAsyncPaginate;
  }

  const input = control ? (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => {
        return (
          <AsyncPaginateInput
            {...{
              ...commonProps,
              ...field,
              value: field.value || placeholder,
              defaultValue: field.value,
              placeholder: placeholder || getLabel(field.value) || (disabled ? '-' : ''),
              onChange: (option) => {
                if (isMulti) {
                  mapDataToFilters(option, field.onChange);

                  return;
                }

                if (onChange) {
                  onChange(getValue(option) || searchText);
                }

                field.onChange(getValue(option) || searchText);
              },
              onBlur: (event) => onBlur && onBlur(event, searchText),
              onKeyDown: (event) => onKeyDown && onKeyDown(event, searchText),
              createOptionPosition: 'first',
              menuPortalTarget: document.body,
            }}
          />
        );
      }}
    />
  ) : (
    <AsyncPaginateInput
      {...{
        ...commonProps,
        name,
        value: isMulti && value ? getValue(value) : value,
        onChange: (option) =>
          isMulti ? mapDataToFilters(option, onChange) : onChange(getValue(option)),
        onBlur,
        onKeyDown: (event) => onKeyDown && onKeyDown(event, searchText),
        createOptionPosition: 'first',
        menuPortalTarget: document.body,
      }}
    />
  );

  return (
    <>
      <InputWrapper disabled={disabled}>
        <Visible when={Boolean(startIcon)}>
          <IconWrapper
            onMouseDown={(event) => onIconClick(event, searchText)}
            disabled={disabled}
            style={iconStyle}
          >
            {startIcon}
          </IconWrapper>
        </Visible>

        {input}

        <Visible when={Boolean(endIcon)}>
          <IconWrapper
            onMouseDown={(event) => onIconClick(event, searchText)}
            style={iconStyle}
            endIcon
          >
            {endIcon}
          </IconWrapper>
        </Visible>
      </InputWrapper>

      {error && showFieldError && <FormError error={error} />}
    </>
  );
};

export default AsyncSelectView;
